import React, { useContext, useEffect, useMemo, useState } from "react";
import "./TicketingSystemInstanceValidation.scss";
import { createColumnHelper } from "@tanstack/react-table";
import { useQuery, useApolloClient } from "@apollo/client";
import { GET_WEBHOOKS_TESTS } from "../../../TicketingSystemsApi";
import TicketId from "../../../../Components/TicketId/TicketId";
import {
  getFormattedDate,
  Tooltip,
  useIcon,
  Table,
  TableCell,
  tableCellTypesEnum,
  TextButton,
  ModalContext,
  type ModalContextType,
} from "@SeemplicityDev/shared";
import TestManualTicket from "./TestManualTicket/TestManualTicket";
import useManualTicket from "../../../../Ticketing/useManualTicket";
import TicketMasterApolloClient from "../../../../TicketMasterApolloClient/TicketMasterApolloClient";
import {
  AutomationSystemsContext,
  type AutomationSystemsContextType,
} from "../../../AutomationSystemsWrapper/AutomationSystemsProvider";

const WEBHOOK_RESULT_OPTIONS = {
  FAIL: "FAIL",
  PASS: "PASS",
  PENDING: "PENDING",
} as const;

type WebhookResultOption =
  (typeof WEBHOOK_RESULT_OPTIONS)[keyof typeof WEBHOOK_RESULT_OPTIONS];

interface WebhookTestResult {
  ticket_id: string;
  ticket_external_id: string;
  ticket_link: string;
  ticket_created_time: string;
  ticket_provider_type: string;
  result: WebhookResultOption;
  error: string;
}

interface WebhookTestRow extends WebhookTestResult {
  ticket: {
    external_id: string;
    link: string;
    ticket_provider_type: string;
  };
}

interface WebhookTestsData {
  ticket_provider_webhook_validation: {
    tests_results: WebhookTestResult[] | null;
  } | null;
}

interface WebhookTestsVariables {
  ticket_provider_id?: number;
}

interface TicketingSystemInstanceValidationProps {
  id?: string;
  type?: string;
  idInt?: number;
}

const TicketingSystemInstanceValidation: React.FC<
  TicketingSystemInstanceValidationProps
> = ({ id, type, idInt }) => {
  const { openModal, closeModal } = useContext(
    ModalContext
  ) as ModalContextType;
  const [testTickets, setTestTickets] = useState<WebhookTestRow[] | null>(null);
  const { apolloClient, endpointConfig } = useContext(
    AutomationSystemsContext
  ) as AutomationSystemsContextType;
  const client = useApolloClient();
  const { getDataSourceIcon } = useIcon();
  const { loading, data, startPolling, stopPolling } = useQuery<
    WebhookTestsData,
    WebhookTestsVariables
  >(GET_WEBHOOKS_TESTS, {
    variables: { ticket_provider_id: idInt },
    skip: !idInt,
  });

  useEffect(() => {
    if (!!data) {
      const tests =
        data?.ticket_provider_webhook_validation?.tests_results?.map((test) => {
          return {
            ...test,
            ticket: {
              external_id: test?.ticket_external_id,
              link: test?.ticket_link,
              ticket_provider_type: test?.ticket_provider_type,
            },
          };
        });

      setTestTickets(tests ?? null);
      const skipPolling = !tests?.find(
        (test) => test.result === WEBHOOK_RESULT_OPTIONS.PENDING
      );

      !skipPolling ? startPolling(10000) : stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [data]);

  const getIcon = (result?: WebhookResultOption, error?: string) => {
    switch (result) {
      case WEBHOOK_RESULT_OPTIONS.PASS:
        return (
          <>
            <Tooltip type="info" content={"Validation Succeeded"}>
              <i className={"seem-icon seem-icon-jira-icon-done"} />
            </Tooltip>
          </>
        );
      case WEBHOOK_RESULT_OPTIONS.FAIL:
        return (
          <>
            <Tooltip content={error}>
              <i className={"seem-icon seem-icon-circle-close"} />
            </Tooltip>
          </>
        );
      case WEBHOOK_RESULT_OPTIONS.PENDING:
        return (
          <>
            <i className="spinner seem-icon seem-icon-spinner" />
            Checking Webhook
          </>
        );

      default:
        return <></>;
    }
  };

  const columnHelper = createColumnHelper<WebhookTestRow>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("ticket", {
        cell: (info) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <TicketId
              id={info?.getValue()?.external_id as string}
              ticketManager={info?.getValue()?.ticket_provider_type}
              logo={
                getDataSourceIcon(
                  info?.getValue()?.ticket_provider_type as string,
                  "S"
                ) as string
              }
              link={info?.getValue()?.link}
            />
          </TableCell>
        ),
        header: "Ticket ID",
        enableSorting: false,
        enableColumnFilter: false,
        size: 400,
      }),
      columnHelper.accessor("result", {
        cell: (info) => {
          const error = info?.row?.original?.error;

          return (
            <TableCell
              type={tableCellTypesEnum.COMPONENT}
              className={"result-cell"}
            >
              {getIcon(info.getValue(), error)}
            </TableCell>
          );
        },
        header: "Webhook Validation",
        enableSorting: false,

        enableColumnFilter: false,
        size: 300,
      }),
      columnHelper.accessor("ticket_created_time", {
        cell: (info) => {
          const RFC1123FormatTimestamp = info.getValue();
          const validISOFormatTimestamp = new Date(
            RFC1123FormatTimestamp
          ).toISOString();
          return (
            <TableCell>
              <Tooltip content={getFormattedDate(validISOFormatTimestamp)}>
                <span>{getFormattedDate(validISOFormatTimestamp)}</span>
              </Tooltip>
            </TableCell>
          );
        },
        header: "Validation Date",
        id: "created_time",
        enableColumnFilter: false,
        sortingFn: "datetime",
      }),
    ],
    [columnHelper, getDataSourceIcon]
  );

  const { onSuccess, onError, onCreateMapping, onCreateMappingError } =
    useManualTicket();

  return (
    <div className={"test-connection-validation"}>
      <div className={"test-connection-header"}>
        <div className={"header-title"}>Validation Test</div>
        <ol className={"test-connection-description"}>
          <li>
            Click on the{" "}
            <span className={"emphasized"}>Create Test Ticket</span> button to
            test ticket creation.
          </li>
          <li>
            After successful ticket creation, Seemplicity validates the webhook
            by updating the ticket's status in the ticketing system.
          </li>
          <li>
            The result will be displayed in the{" "}
            <span className={"emphasized"}>Webhook Validation</span> column.
          </li>
        </ol>
        <TextButton
          buttonStyle={"primary"}
          label={"Create Test Ticket"}
          type={"button"}
          data-testid={"test-connection-validation"}
          onClick={() => {
            openModal({
              title: "Create Test Ticket",
              className: "modal-manual-ticket",
              overlay: true,
              size: "l",
              component: (
                <TicketMasterApolloClient apolloClient={apolloClient}>
                  <TestManualTicket
                    ticketProviderId={id}
                    ticketProviderType={type}
                    closeModal={closeModal}
                    onSuccess={(ticketData) => {
                      onSuccess(ticketData);
                      client.refetchQueries({
                        include: ["GetWebhookTests"],
                      });
                    }}
                    onError={onError}
                    onCreateMapping={onCreateMapping}
                    onCreateMappingError={onCreateMappingError}
                    endpointConfig={endpointConfig}
                  />
                </TicketMasterApolloClient>
              ),
            });
          }}
          title={"Create test ticket"}
        />
      </div>
      {!!testTickets?.length && (
        <Table
          columns={columns}
          initialSortBy={[{ id: "created_time" }]}
          data={testTickets}
          loading={loading}
          loaderRowsAmount={3}
          data-testid="test-ticket-validation"
        />
      )}
    </div>
  );
};

export default TicketingSystemInstanceValidation;
